import { zodResolver } from '@hookform/resolvers/zod/dist/zod'
import { LoadingButton } from '@mui/lab'
import { Box, Grid, Stack } from '@mui/material'
import { updateUser } from 'api/users'
import FormInput from 'components/form-elements/FormInput'
import { EMAIL_IS_INVALID, FIELD_IS_REQUIRED } from 'const'
import { CohortsMultiSelect } from 'features/Cohorts'
import { useCallback, useMemo } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useMutation } from 'react-query'
import { OrganizationalUser, UserTypeLabels, UserUpdateRequest } from 'types'
import { number, object, string, TypeOf } from 'zod'

const formSchema = object({
  firstname: string().min(1, FIELD_IS_REQUIRED),
  lastname: string().min(1, FIELD_IS_REQUIRED),
  email: string().email(EMAIL_IS_INVALID),
  cohorts: object({
    label: string(),
    value: number(),
  })
    .array()
    .optional(),
})

type FormType = TypeOf<typeof formSchema>

export function UserOrgDetailsForm({
  user,
  isAdminPage,
  onAfterUpdate,
}: {
  user: OrganizationalUser
  isAdminPage: boolean
  onAfterUpdate: () => void
}) {
  const defaultCohorts = useMemo(() => {
    if (!user.cohorts) return []

    return user.cohorts.map((cohort) => ({ label: cohort.name, value: cohort.id }))
  }, [user])

  const defaultValues = useMemo(() => {
    return {
      firstname: user.firstname,
      lastname: user.lastname,
      email: user.email,
      organizationName: user.organizationName,
      role: UserTypeLabels[user.type[0]],
      ownedAccessCodes: user.ownedAccessCodes.map((accessCode) => accessCode.code).join(', '),
      cohorts: defaultCohorts,
    }
  }, [user, defaultCohorts])

  const methods = useForm<FormType>({
    mode: 'all',
    resolver: zodResolver(formSchema),
    defaultValues,
  })

  const saveUserMutation = useMutation(updateUser)

  const getRequestData = useCallback(() => {
    const values = methods.getValues()

    const data: UserUpdateRequest = {
      id: user.id,
      firstname: values.firstname,
      lastname: values.lastname,
      email: values.email,
    }

    if (isAdminPage) {
      data.cohortIds = values.cohorts?.map((item) => item.value) || []
    }

    return data
  }, [methods, user, isAdminPage])

  const handleCheckUpdate = useCallback(async () => {
    const request = getRequestData()
    await saveUserMutation.mutateAsync(request)
    onAfterUpdate()
  }, [getRequestData, onAfterUpdate, saveUserMutation])

  return (
    <FormProvider {...methods}>
      <Box
        component="form"
        onSubmit={methods.handleSubmit(handleCheckUpdate)}
        noValidate
        width="100%"
        mt={4}
      >
        <Grid container spacing={4}>
          <Grid item sm={12} md={12} lg={6} xl={6}>
            <Stack spacing={2}>
              <FormInput label="First Name" name="firstname" required fullWidth />
              <FormInput label="Last Name" name="lastname" required fullWidth />
              <FormInput label="Email" name="email" required fullWidth autoComplete="off" />

              {isAdminPage && <CohortsMultiSelect defaultValue={defaultValues.cohorts} />}

              <FormInput label="Organization" name="organizationName" disabled fullWidth />
              <FormInput label="Role" name="role" disabled fullWidth />
              <FormInput label="Access Codes" name="ownedAccessCodes" disabled fullWidth />
            </Stack>
          </Grid>

          <Grid item sm={12} md={12} lg={12} xl={12}>
            <LoadingButton
              variant="contained"
              type="submit"
              disabled={saveUserMutation.isLoading}
              loading={saveUserMutation.isLoading}
              sx={{ width: '120px' }}
            >
              Save
            </LoadingButton>
          </Grid>
        </Grid>
      </Box>
    </FormProvider>
  )
}
